import { useEffect, useRef } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import PinIcon from '@/components/icons/PinIcon';
import type { WebinarEventHistoryDto } from '@/features/live/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { isFollowingBottom } from '@/lib/chatScrollAnchor';

interface ChatMessage {
  authorDisplayName: string;
  content: string;
  offsetSeconds: number;
  messageId: string;
  repliedToId?: number;
}

interface ChatReactions {
  [messageId: string]: {
    [emoji: string]: number;
  };
}

function getChatMessageContent(payload: Record<string, unknown>): string | null {
  if (typeof payload.content === 'string' && payload.content.trim()) {
    return payload.content;
  }
  if (typeof payload.text === 'string' && payload.text.trim()) {
    return payload.text;
  }
  return null;
}

function getChatMessageId(payload: Record<string, unknown>): string {
  const messageId =
    (payload.resourceId as number | undefined) || (payload.messageId as number | undefined);
  return messageId ? String(messageId) : '';
}

/** Tolérance pour considérer que le lecteur est collé en bas de liste. */
const CHAT_BOTTOM_THRESHOLD_PX = 10;

/** Au-delà, l'écart entre deux mesures de position vidéo est un saut, pas une lecture normale. */
const CHAT_SEEK_THRESHOLD_S = 2;

const getVisibleChatMessages = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ChatMessage[] => {
  if (!eventHistory?.events) {
    return [];
  }

  return eventHistory.events
    .filter((event) => {
      if (event.eventType !== 'CHAT_MESSAGE' || event.offsetSeconds > currentTime) {
        return false;
      }
      const content = event.payload ? getChatMessageContent(event.payload) : null;
      return Boolean(event.authorDisplayName && content);
    })
    .map((event) => ({
      authorDisplayName: event.authorDisplayName!,
      content: getChatMessageContent(event.payload)!,
      offsetSeconds: event.offsetSeconds,
      messageId: getChatMessageId(event.payload),
      repliedToId: event.payload?.repliedToId as number | undefined,
    }))
    .sort((a, b) => a.offsetSeconds - b.offsetSeconds);
};

const getChatReactions = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ChatReactions => {
  if (!eventHistory?.events) {
    return {};
  }

  const reactions: ChatReactions = {};

  eventHistory.events
    .filter(
      (event) =>
        event.eventType === 'CHAT_REACTION' &&
        event.offsetSeconds <= currentTime &&
        (event.payload?.messageId || event.payload?.resourceId) &&
        event.payload?.reaction,
    )
    .sort((a, b) => a.offsetSeconds - b.offsetSeconds)
    .forEach((event) => {
      const messageId = String(
        (event.payload?.resourceId as number | undefined) ||
          (event.payload?.messageId as number | undefined) ||
          0,
      );
      const reaction = event.payload.reaction as string;

      if (!reactions[messageId]) {
        reactions[messageId] = {};
      }

      if (!reactions[messageId][reaction]) {
        reactions[messageId][reaction] = 0;
      }

      reactions[messageId][reaction] += 1;
    });

  return reactions;
};

const getPinnedMessageIds = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): Set<string> => {
  if (!eventHistory?.events) {
    return new Set();
  }

  const pinnedIds = new Set<string>();

  [...eventHistory.events]
    .filter((event) => event.offsetSeconds <= currentTime)
    .sort((a, b) => a.offsetSeconds - b.offsetSeconds)
    .forEach((event) => {
      if (event.eventType === 'MESSAGE_PIN') {
        const messageId =
          event.payload?.resourceId != null
            ? (event.payload.resourceId as number)
            : (event.payload?.messageId as number | undefined);
        if (messageId) {
          pinnedIds.add(String(messageId));
        }
      } else if (event.eventType === 'MESSAGE_UNPIN') {
        const messageId =
          event.payload?.resourceId != null
            ? (event.payload.resourceId as number)
            : (event.payload?.messageId as number | undefined);
        if (messageId) {
          pinnedIds.delete(String(messageId));
        }
      }
    });

  return pinnedIds;
};

interface ChatViewReplayProps {
  eventHistory: WebinarEventHistoryDto | null;
  currentTime: number;
  visualIdentity?: WorkspaceVisualIdentity | null | any;
}

export default function ChatViewReplay({
  eventHistory,
  currentTime,
  visualIdentity,
}: ChatViewReplayProps) {
  const { t } = useWebinarT('webinars');
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const visibleMessages = getVisibleChatMessages(eventHistory, currentTime);
  const chatReactions = getChatReactions(eventHistory, currentTime);
  const pinnedMessageIds = getPinnedMessageIds(eventHistory, currentTime);

  // Un saut dans la vidéo rejoue un autre moment du chat : la position de lecture précédente n'a
  // plus de sens, on repart du bas.
  const previousTimeRef = useRef(currentTime);
  useEffect(() => {
    const el = chatScrollRef.current;
    const jumped = Math.abs(currentTime - previousTimeRef.current) > CHAT_SEEK_THRESHOLD_S;
    previousTimeRef.current = currentTime;
    if (el && jumped) {
      el.scrollTop = 0;
    }
  }, [currentTime]);

  // Suivi du direct simulé, mais seulement si le lecteur est déjà en bas de liste : la lecture
  // avance ~4 fois par seconde, et recaler à chaque tick rendait toute relecture impossible.
  // Conteneur en colonne inversée : le bas de liste, c'est scrollTop 0 (négatif en remontant
  // selon les navigateurs).
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    if (isFollowingBottom(el.scrollTop, CHAT_BOTTOM_THRESHOLD_PX)) {
      el.scrollTop = 0;
    }
  }, [visibleMessages.length]);

  const findMessageById = (resourceId: number): ChatMessage | undefined => {
    if (!eventHistory?.events) {
      return undefined;
    }

    const event = eventHistory.events.find((e) => {
      if (e.eventType !== 'CHAT_MESSAGE' || e.offsetSeconds > currentTime) {
        return false;
      }
      const content = e.payload ? getChatMessageContent(e.payload) : null;
      return (
        ((e.payload?.resourceId as number | undefined) === resourceId ||
          (e.payload?.messageId as number | undefined) === resourceId) &&
        Boolean(e.authorDisplayName && content)
      );
    });

    if (!event) {
      return undefined;
    }

    return {
      authorDisplayName: event.authorDisplayName!,
      content: getChatMessageContent(event.payload)!,
      offsetSeconds: event.offsetSeconds,
      messageId: getChatMessageId(event.payload),
      repliedToId: event.payload?.repliedToId as number | undefined,
    };
  };

  const pinnedMessages = visibleMessages.filter((msg) => pinnedMessageIds.has(msg.messageId));
  const unpinnedMessages = visibleMessages.filter((msg) => !pinnedMessageIds.has(msg.messageId));

  return (
    <>
      {/* messagePIN */}
      {pinnedMessages.length > 0 && (
        <div className="space-y-2 py-2">
          {pinnedMessages.map((message) => {
            const messageId = message.messageId;
            const reactions = messageId ? chatReactions[messageId] : undefined;
            const repliedToMessage = message.repliedToId
              ? findMessageById(message.repliedToId)
              : undefined;

            return (
              <div
                key={`pinned-${messageId || message.offsetSeconds}`}
                className="flex flex-col relative p-2 mx-2 rounded-t-lg rounded-br-lg border-2"
                style={{
                  backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF',
                  borderColor: visualIdentity?.contrastColor || '#2C6CFF',
                }}
              >
                <div className="flex items-center gap-1">
                  <PinIcon size={12} color="#2C6CFF" />

                  <span
                    className={
                      false
                        ? 'text-xs font-normal text-yellow-600'
                        : `text-xs font-normal ${
                            visualIdentity?.theme === 'LIGHT' ? 'text-secondary' : 'text-white'
                          }`
                    }
                  >
                    {message.authorDisplayName}
                  </span>
                </div>

                {reactions && Object.keys(reactions).length > 0 && (
                  <div className="absolute top-1 right-1">
                    <div className="flex items-center gap-2">
                      {Object.entries(reactions).map(([emoji, count]) =>
                        count > 0 ? (
                          <span
                            key={emoji}
                            className="rounded-full py-0.5 px-1.5 text-xs"
                            style={{
                              color: visualIdentity?.theme === 'LIGHT' ? '#000000' : '#FFFFFF',
                              backgroundColor:
                                visualIdentity?.theme === 'LIGHT'
                                  ? 'rgba(31, 31, 31, 0.03)'
                                  : 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            {emoji} {count}
                          </span>
                        ) : null,
                      )}
                    </div>
                  </div>
                )}

                <div>
                  {repliedToMessage && (
                    <div
                      className="border-l-4 rounded-r-md rounded-l-sm py-1 my-0.5"
                      style={{
                        borderColor: visualIdentity?.contrastColor || '#2C6CFF',
                        backgroundColor:
                          visualIdentity?.theme === 'LIGHT'
                            ? 'rgba(31, 31, 31, 0.08)'
                            : 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <div className="pl-1">
                        <p
                          className="text-[10px] font-semibold"
                          style={{
                            color: visualIdentity?.contrastColor || '#2C6CFF',
                          }}
                        >
                          {repliedToMessage.authorDisplayName || t('live.chat.previousMessage')}
                        </p>
                        <p
                          className={`text-xs font-normal ${
                            visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
                          }`}
                        >
                          {repliedToMessage.content}
                        </p>
                      </div>
                    </div>
                  )}
                  <p className={visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}>
                    {message.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* message */}
      <div
        ref={chatScrollRef}
        className=" pt-2  flex-1 overflow-y-auto min-h-0 space-y-2 flex flex-col-reverse relative"
      >
        <div className="flex flex-col space-y-2 justify-end flex-1">
          {unpinnedMessages.map((message) => {
            const messageId = message.messageId;
            const reactions = messageId ? chatReactions[messageId] : undefined;
            const repliedToMessage = message.repliedToId
              ? findMessageById(message.repliedToId)
              : undefined;

            return (
              <div
                key={messageId || `msg-${message.offsetSeconds}-${message.authorDisplayName}`}
                className="flex flex-col relative p-2 mx-2 rounded-t-lg rounded-br-lg bg-white"
                style={{
                  backgroundColor: visualIdentity?.backgroundColor,
                }}
              >
                <div className="flex items-center gap-1">
                  <span
                    className={
                      false
                        ? 'text-xs font-normal text-yellow-600'
                        : `text-xs font-normal ${
                            visualIdentity?.theme === 'LIGHT' ? 'text-secondary' : 'text-white'
                          }`
                    }
                  >
                    {message.authorDisplayName}
                  </span>
                </div>

                {reactions && Object.keys(reactions).length > 0 && (
                  <div className="absolute top-1 right-1">
                    <div className="flex items-center gap-2">
                      {Object.entries(reactions).map(([emoji, count]) =>
                        count > 0 ? (
                          <span
                            key={emoji}
                            className="rounded-full py-0.5 px-1.5 text-xs"
                            style={{
                              color: visualIdentity?.theme === 'LIGHT' ? '#000000' : '#FFFFFF',
                              backgroundColor:
                                visualIdentity?.theme === 'LIGHT'
                                  ? 'rgba(31, 31, 31, 0.03)'
                                  : 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            {emoji} {count}
                          </span>
                        ) : null,
                      )}
                    </div>
                  </div>
                )}

                <div>
                  {repliedToMessage && (
                    <div
                      className="border-l-4 rounded-r-md rounded-l-sm py-1 my-0.5"
                      style={{
                        borderColor: visualIdentity?.contrastColor || '#2C6CFF',
                        backgroundColor:
                          visualIdentity?.theme === 'LIGHT'
                            ? 'rgba(31, 31, 31, 0.08)'
                            : 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <div className="pl-1">
                        <p
                          className="text-[10px] font-semibold"
                          style={{
                            color: visualIdentity?.contrastColor || '#2C6CFF',
                          }}
                        >
                          {repliedToMessage.authorDisplayName || t('live.chat.previousMessage')}
                        </p>
                        <p
                          className={`text-xs font-normal ${
                            visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
                          }`}
                        >
                          {repliedToMessage.content}
                        </p>
                      </div>
                    </div>
                  )}
                  <p className={visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}>
                    {message.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* textarea */}
      <div className="pt-2 shrink-0">
        <div
          className="w-full flex  h-16 items-center justify-center px-2 rounded-t-[20px] border-t border-l border-r"
          style={{
            backgroundColor:
              visualIdentity?.theme === 'LIGHT'
                ? '#FFFFFF'
                : visualIdentity?.backgroundColor || '#FFFFFF',
            borderColor: visualIdentity?.contrastColor || '#FFFFFF',
          }}
        >
          <p className="text-sm text-center text-tertiary">{t('live.chat.unavailableInReplay')}</p>
        </div>
      </div>
    </>
  );
}
